// INTEL CONFIDENTIAL
// Copyright (C) 2023 Intel Corporation

import React from 'react'
import './Offerings.scss'
export interface ResourceAction {
  label: string
  type: 'button' | 'link' | 'links'
  variant?: string
  openInNewTab?: boolean
  redirectTo: string
  style?: any
}
interface OfferingsResource {
  headerText?: string
  title?: string
  bodyText?: string | string[]
  footerText?: string
  actions?: ResourceAction[]
}

export interface OfferingsProps {
  title?: string
  text?: string
  offerings?: OfferingsResource[]
  salesText?: string
  showSalesButton?: boolean
  signupText?: string
  showSignupButton?: boolean
  applyNowText?: string
  showApplyNowButton?: boolean
  viewAllText?: string
  showViewAllButton?: boolean
  customClass?: string
  redirectTo?: string
}

const Offerings: React.FC<OfferingsProps> = ({
  title,
  text,
  offerings = [],
  salesText,
  showSalesButton,
  signupText,
  showSignupButton,
  applyNowText,
  showApplyNowButton,
  viewAllText,
  showViewAllButton = false,
  customClass,
  redirectTo
}): JSX.Element => {
  return (
    <div className={`product-offerings-container gap-s8 p-s8 p-sm-s10 ${customClass ?? ''}`}>
      <div className="internal-container gap-s7 gap-sm-s8">
        <div className="product-offerings-title gap-s3">
          <>
            <h2>
              <span className="display-6">{title}</span>
            </h2>
            {text && <span className="lead">{text}</span>}
          </>
        </div>
        {offerings?.length > 0 && (
          <div className="product-offerings-items-container flex-xs-column flex-sm-row">
            {offerings.map((item: any, index) => (
              <div
                className={`product-offerings-item-container p-s5 p-sm-s8 gap-s6 ${offerings.length > 3 ? `col-sm-${offerings.length === 4 ? '3' : '4'} col-12 multi-row` : 'w-100 w-sm-auto'}`}
                key={index}
              >
                <div className="d-flex flex-column align-self-stretch gap-s7">
                  {item.headerText && <h3 className="h6">{item.headerText}</h3>}
                  {item.title && <h3 className="h3">{item.title}</h3>}
                  {!Array.isArray(item.bodyText) && <span>{item.bodyText}</span>}
                  {Array.isArray(item.bodyText) && (
                    <ul className="product-offerings-item-list gap-s3 ps-0">
                      {item.bodyText.map((e: string, idx: number) => (
                        <li key={idx}>{e}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {item.footerText && <span className="h5">{item.footerText}</span>}
                {item.actions?.map((action: ResourceAction, actionIdx: number) => (
                  <a
                    key={actionIdx}
                    className={
                      action.type === 'link' ? 'link' : `btn btn-${action?.variant ?? 'outline-primary'} btn-lg`
                    }
                    href={action.redirectTo}
                    target={action.openInNewTab ? '_blank' : '_self'}
                    rel={action.openInNewTab ? 'noreferrer' : undefined}
                    style={action.style ?? undefined}
                    intc-id={`${action.type}-${action.label}-${title}`.replaceAll(' ', '-')}
                    data-wap_ref={`${action.type}-${action.label}-${title}`.replaceAll(' ', '-')}
                    role={action.type}
                    aria-label={`Go to ${action.redirectTo}`}
                  >
                    {action.label}
                  </a>
                ))}
              </div>
            ))}
          </div>
        )}
        <div className="d-flex flex-xs-column flex-sm-row align-items-center gap-s8">
          {(salesText ?? showSalesButton) && (
            <div className="product-offerings-footer-container gap-s5">
              <span className="align-self-center fw-semibold">{salesText}</span>
              <div className="flex-center px-s5">
                <a
                  href={process.env.CONTACT_SALES_URL}
                  className="btn btn-primary btn-lg"
                  rel="noreferrer"
                  intc-id="btn-landing-offerings-sales"
                  aria-label="Go to Contact"
                  data-wap_ref="btn-landing-offerings-sales"
                  target="_blank"
                >
                  Contact
                </a>
              </div>
            </div>
          )}
          {(signupText ?? showSignupButton) && (
            <div className="product-offerings-footer-container gap-s5">
              <span className="align-self-center fw-semibold">{signupText}</span>
              <div className="flex-center px-s5">
                <a
                  href={process.env.CONSOLE_URL}
                  className="btn btn-primary btn-lg"
                  rel="noreferrer"
                  target="_blank"
                  intc-id="btn-landing-offerings-signup"
                  aria-label="Go to sign up"
                  data-wap_ref="btn-landing-offerings-signup"
                >
                  Sign up
                </a>
              </div>
            </div>
          )}
          {(applyNowText ?? showApplyNowButton) && (
            <div className="product-offerings-footer-container gap-s5">
              <span className="align-self-center fw-semibold">{applyNowText}</span>
              <div className="flex-center px-s5">
                <a
                  href={redirectTo ?? process.env.CONSOLE_URL}
                  className="btn btn-primary btn-lg"
                  rel="noreferrer"
                  target="_blank"
                  intc-id="btn-landing-offerings-apply-now"
                  aria-label="Go to apply now"
                  data-wap_ref="btn-landing-offerings-apply-now"
                >
                  Apply now
                </a>
              </div>
            </div>
          )}
          {(viewAllText ?? showViewAllButton) && (
            <div className="product-offerings-footer-container gap-s5">
              <div className="flex-center px-s5">
                <a
                  href="/products"
                  className="btn btn-outline-primary btn-lg"
                  intc-id="btn-landing-offerings-viewall"
                  aria-label="Go to products page"
                  data-wap_ref="btn-landing-offerings-viewall"
                >
                  {viewAllText}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Offerings
